// Pure helpers for the "My assessment" journey card. Kept framework-free so
// both the military Dashboard and CommercialDashboard can share one notion
// of "how many days until the real test, and what should the card show".

// Math.round, not ceil: both ends are local midnights, so a span crossing a
// daylight-saving change is off a whole day by an hour, and ceil counted
// the autumn change as an extra day.
export const daysUntilAssessment = (dateValue, now = new Date()) => {
  if (!dateValue) return null;
  const target = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

// Journey stages, in the order a booking normally moves through. "none"
// covers both "never set one" and "the last booking was cancelled" -- both
// cases fall out naturally because a cancelled/taken booking stops being the
// active one the backend returns (see _get_active_assessment_booking).
export const ASSESSMENT_JOURNEY_STAGES = Object.freeze({
  NONE: 'none',
  WEEKS_AWAY: 'weeks-away',
  FINAL_WEEK: 'final-week',
  DAY_BEFORE: 'day-before',
  AWAITING_OUTCOME: 'awaiting-outcome'
});

export const getAssessmentJourneyStage = (booking, now = new Date()) => {
  if (!booking || !booking.date) return ASSESSMENT_JOURNEY_STAGES.NONE;
  const days = daysUntilAssessment(booking.date, now);
  if (days === null) return ASSESSMENT_JOURNEY_STAGES.NONE;
  if (days < 0) return ASSESSMENT_JOURNEY_STAGES.AWAITING_OUTCOME;
  if (days <= 1) return ASSESSMENT_JOURNEY_STAGES.DAY_BEFORE;
  if (days <= 7) return ASSESSMENT_JOURNEY_STAGES.FINAL_WEEK;
  return ASSESSMENT_JOURNEY_STAGES.WEEKS_AWAY;
};

export const formatAssessmentDate = (dateValue) => (
  dateValue
    ? new Date(`${dateValue}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Not set'
);

// "Completed" means a real assessment-mode attempt, not practice -- matches
// the same mode === 'assessment' filter cbatScoring.js and Dashboard.js
// already use to separate scored attempts from free practice.
export const hasCompletedAnAssessment = (history) => (
  Array.isArray(history) && history.some((attempt) => attempt.mode === 'assessment')
);

export const formatCountdownSummary = (days) => {
  if (days === null) return 'Set a date to pace your preparation';
  if (days < 0) return 'Assessment date has passed';
  if (days === 0) return 'Assessment day';
  return `${days} day${days === 1 ? '' : 's'} remaining`;
};

// What saving a Plan Builder plan should do to the assessment booking, since
// the dashboard reads the booking, not preparation preferences -- without
// this, a candidate who saved a plan with a date was asked for that same
// date again on arrival. Returns null (nothing to do), a 'save' (create the
// booking, or edit the active one in place, keeping its created_at and so
// its plan anchor), or -- when the active booking's own date has already
// passed -- a 'reschedule', so a new sitting gets a new booking instead of
// silently overwriting the old one's record. Military only, like the
// journey card itself.
export const planBookingAction = (inputs, existingBooking = null, now = new Date()) => {
  if (!inputs || inputs.active_family === 'commercial') return null;
  const date = inputs.assessment_date || null;
  const days = daysUntilAssessment(date, now);
  if (days === null || days < 0) return null;

  const provider = inputs.military_profile || null;
  const role = inputs.military_role || null;
  if (
    existingBooking?.date === date
    && (existingBooking.provider || null) === provider
    && (existingBooking.role || null) === role
  ) return null;

  const booking = { family: 'military', provider, role, date, date_type: existingBooking?.date_type || 'estimated' };
  const existingDays = daysUntilAssessment(existingBooking?.date, now);
  if (existingDays !== null && existingDays < 0) {
    // The status endpoint copies provider/role from the old booking, so the
    // follow-up save applies this plan's own provider/role to the new one.
    return { type: 'reschedule', status: { status: 'rescheduled', date, date_type: booking.date_type }, booking };
  }
  return { type: 'save', booking };
};
